import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Target, Plus, ChevronRight } from "lucide-react";
import { API } from "@/lib/api";
import { AddGoalModal } from "./AddGoalModal";
import { GoalDetailModal } from "./GoalDetailModal";
import { UpgradeGoalsModal } from "./UpgradeGoalsModal";

/**
 * GoalsWidget — compact row of up to 5 goal cards on the athlete dashboard.
 * One glance shows: current → target + progress + motivational copy.
 * Tap any card to log a new value or edit the target.
 */
export const GoalsWidget = () => {
  const [goals, setGoals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [selectedGoal, setSelectedGoal] = useState(null);
  const [upgradePrompt, setUpgradePrompt] = useState(null); // {tier, missing_goals}

  const fetchGoals = async () => {
    try {
      const res = await fetch(`${API}/athlete/goals`, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setGoals(data.goals || []);
      }
    } catch (e) {
      console.error("goals fetch failed", e);
    } finally {
      setLoading(false);
    }
  };

  const fetchUpgradePrompt = async () => {
    try {
      const res = await fetch(`${API}/athlete/goals/upgrade-prompt`, { credentials: "include" });
      if (res.ok) {
        const d = await res.json();
        if (d.show && (d.missing_goals || []).length > 0) {
          setUpgradePrompt({ tier: d.tier, missing_goals: d.missing_goals });
        }
      }
    } catch (e) {
      console.error("upgrade prompt fetch failed", e);
    }
  };

  useEffect(() => {
    // Fetch goals first (which may lazy-apply tier template), then check
    // for upgrade-prompt opportunities.
    (async () => {
      await fetchGoals();
      await fetchUpgradePrompt();
    })();
  }, []);

  const topGoals = goals.slice(0, 5);
  const hasMore = goals.length > 5;
  const needsConfirmCount = goals.filter((g) => g.needs_confirmation).length;

  if (loading) return null;

  return (
    <>
      <div className="mb-8" data-testid="goals-widget">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Target className="w-4 h-4 text-amber-400" />
            <h2 className="font-heading text-sm uppercase tracking-[0.2em] font-bold text-white">
              My Goals
            </h2>
          </div>
          <button
            onClick={() => setShowAdd(true)}
            className="flex items-center gap-1 text-xs text-text-secondary hover:text-amber-400 transition-colors"
            data-testid="goals-add-btn"
          >
            <Plus className="w-3.5 h-3.5" />
            Add goal
          </button>
        </div>

        {needsConfirmCount > 0 && (
          <div className="mb-3 bg-amber-500/10 border border-amber-500/30 rounded-lg px-3 py-2 flex items-center gap-2" data-testid="goals-needs-confirm-banner">
            <span className="text-base">👋</span>
            <p className="text-xs text-amber-300">
              <span className="font-bold">Set your real numbers</span> — your coach pre-loaded {needsConfirmCount} starter goal{needsConfirmCount === 1 ? "" : "s"}. Tap each card and log your current value.
            </p>
          </div>
        )}

        {topGoals.length === 0 ? (
          <button
            onClick={() => setShowAdd(true)}
            className="w-full bg-surface border border-dashed border-border hover:border-amber-500/50 rounded-xl p-6 text-center transition-colors group"
            data-testid="goals-empty-cta"
          >
            <Target className="w-8 h-8 text-text-muted group-hover:text-amber-400 mx-auto mb-2 transition-colors" />
            <p className="font-heading text-base font-bold text-white mb-1">
              Set your first goal
            </p>
            <p className="text-xs text-text-secondary">
              Track what matters — bodyweight, bench, vertical, or anything else.
            </p>
          </button>
        ) : (
          <div className="flex gap-3 overflow-x-auto scrollbar-hide -mx-4 px-4 sm:mx-0 sm:px-0 sm:grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 pb-1">
            {topGoals.map((goal) => (
              <GoalCard
                key={goal.goal_id}
                goal={goal}
                onClick={() => setSelectedGoal(goal)}
              />
            ))}
            {hasMore && (
              <div className="flex-shrink-0 flex items-center justify-center bg-surface border border-border rounded-xl px-4 py-3 text-xs text-text-secondary min-w-[120px]">
                +{goals.length - 5} more
              </div>
            )}
          </div>
        )}
      </div>

      <AnimatePresence>
        {showAdd && (
          <AddGoalModal
            onClose={() => setShowAdd(false)}
            onCreated={() => {
              setShowAdd(false);
              fetchGoals();
            }}
          />
        )}
        {selectedGoal && (
          <GoalDetailModal
            goal={selectedGoal}
            onClose={() => {
              setSelectedGoal(null);
              fetchGoals();
            }}
            onChanged={fetchGoals}
            onDeleted={() => {
              setSelectedGoal(null);
              fetchGoals();
            }}
          />
        )}
        {upgradePrompt && (
          <UpgradeGoalsModal
            tier={upgradePrompt.tier}
            missingGoals={upgradePrompt.missing_goals}
            onClose={() => setUpgradePrompt(null)}
            onAccepted={() => {
              setUpgradePrompt(null);
              fetchGoals();
            }}
          />
        )}
      </AnimatePresence>
    </>
  );
};

// ============================================================
const GoalCard = ({ goal, onClick }) => {
  const pct = Math.max(0, Math.min(100, goal.progress_pct || 0));
  const isComplete = pct >= 100;

  return (
    <motion.button
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      whileTap={{ scale: 0.98 }}
      onClick={onClick}
      className={`flex-shrink-0 w-[260px] sm:w-auto text-left bg-surface border rounded-xl p-4 transition-colors ${
        isComplete
          ? "border-emerald-500/40 hover:border-emerald-500/70"
          : "border-border hover:border-amber-500/50"
      }`}
      data-testid={`goal-card-${goal.goal_id}`}
    >
      <div className="flex items-center justify-between mb-2 gap-1">
        <p className="text-[10px] uppercase tracking-widest text-text-muted font-bold truncate flex-1">
          {goal.title}
        </p>
        {goal.needs_confirmation && (
          <span className="text-[9px] uppercase tracking-widest bg-amber-500/20 text-amber-400 px-1.5 py-0.5 rounded-full font-bold flex-shrink-0">
            Confirm
          </span>
        )}
        <ChevronRight className="w-3.5 h-3.5 text-text-muted flex-shrink-0" />
      </div>
      <div className="flex items-baseline gap-1.5 mb-2">
        <span className="font-heading text-2xl font-black text-white">
          {formatValue(goal.current_value)}
        </span>
        <span className="text-xs text-text-muted">{goal.unit}</span>
        <span className="text-xs text-text-muted mx-1">→</span>
        <span className="text-sm text-text-secondary">
          {formatValue(goal.target_value)} {goal.unit}
        </span>
      </div>
      <div className="h-1.5 bg-[#1E293B] rounded-full overflow-hidden mb-1.5">
        <div
          className={`h-full rounded-full transition-all duration-500 ${
            isComplete
              ? "bg-gradient-to-r from-emerald-500 to-emerald-400"
              : "bg-gradient-to-r from-amber-500 to-amber-400"
          }`}
          style={{ width: `${pct}%` }}
        />
      </div>
      <p
        className={`text-xs truncate ${
          isComplete ? "text-emerald-400 font-bold" : "text-text-secondary"
        }`}
      >
        {goal.distance_label}
      </p>
    </motion.button>
  );
};

const formatValue = (v) => {
  if (v == null) return "—";
  return Number.isInteger(v) ? v : Number(v).toFixed(1);
};

export default GoalsWidget;
